import Joi from 'joi';

import { translatedMessages } from '@/i18n';
import { type AddressFormValues } from '@/types/location';
import { BRAZIL_STATES, DEFAULT_LIMIT, DEFAULT_OFFSET, regex } from '@/utils/data';

const NS = 'utils.schemas';

const optionalAddressText = (max: number) =>
  Joi.string()
    .trim()
    .max(max)
    .allow('')
    .error(translatedMessages(NS, { 'string.max': 'tooLong' }));

export class CommonValidationSchemas {
  static pagination = {
    offset: Joi.number().integer().min(0).default(DEFAULT_OFFSET),
    limit: Joi.number().integer().min(1).max(100).default(DEFAULT_LIMIT),
  };
  static search = Joi.string().allow('', null).trim().max(255).optional();
  /** Messages resolve at validation time, so this stays a plain constant. */
  static password = Joi.string()
    .pattern(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=.,?:])[A-Za-z\d!@#$%^&*()_+\-=.,?:]{8,}$/,
    )
    .required()
    .error(
      translatedMessages(NS, {
        'string.empty': 'passwordRequired',
        'any.required': 'passwordRequired',
        'string.pattern.base': 'passwordPattern',
      }),
    );
  /**
   * The address of `components/forms/AddressFields` (event address, offer locations). Mirrors the
   * API `partnerAddressFields`: street, city and UF required; CEP optional but valid.
   */
  static address = Joi.object<AddressFormValues>({
    postalCode: Joi.string()
      .trim()
      .pattern(regex.postalCode)
      .allow('')
      .error(translatedMessages(NS, { 'string.pattern.base': 'postalCodeInvalid' })),
    street: Joi.string()
      .trim()
      .max(300)
      .required()
      .error(
        translatedMessages(NS, {
          'string.empty': 'streetRequired',
          'any.required': 'streetRequired',
          'string.max': 'tooLong',
        }),
      ),
    number: optionalAddressText(20),
    complement: optionalAddressText(200),
    neighborhood: optionalAddressText(120),
    city: Joi.string()
      .trim()
      .max(120)
      .required()
      .error(
        translatedMessages(NS, {
          'string.empty': 'cityRequired',
          'any.required': 'cityRequired',
          'string.max': 'tooLong',
        }),
      ),
    state: Joi.string()
      .valid(...BRAZIL_STATES)
      .required()
      .error(
        translatedMessages(NS, {
          'any.only': 'stateRequired',
          'string.empty': 'stateRequired',
          'any.required': 'stateRequired',
        }),
      ),
  });
}
